import {
  ClerkProvider,
  SignIn,
  UserButton,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from '@clerk/clerk-react'
import { createContext, useContext, useMemo } from 'react'

import { runtimeConfigValue } from './runtime-config'

export interface AppAuthUser {
  id: string
  firstName?: string | null
  fullName?: string | null
}

interface AppAuthValue {
  isLoaded: boolean
  isSignedIn: boolean
  getToken: () => Promise<string | null>
  user: AppAuthUser | null
  isPreviewMode: boolean
}

const AppAuthContext = createContext<AppAuthValue | null>(null)

const clerkPubKey = runtimeConfigValue(
  'VITE_CLERK_PUBLISHABLE_KEY',
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
)
const previewEnabled = import.meta.env.VITE_ENABLE_PREVIEW_AUTH === 'true'
const previewUserName = import.meta.env.VITE_PREVIEW_USER_NAME || 'Preview User'
const previewBearerToken = import.meta.env.VITE_PREVIEW_BEARER_TOKEN || null

function PreviewAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded: true,
      isSignedIn: true,
      getToken: async () => previewBearerToken,
      user: {
        id: 'preview-user',
        firstName: previewUserName,
        fullName: previewUserName,
      },
      isPreviewMode: true,
    }),
    [],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

function ClerkAuthBridge({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useClerkAuth()
  const { user } = useClerkUser()

  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded,
      isSignedIn: !!isSignedIn,
      getToken: async () => {
        try {
          return await getToken()
        } catch (error) {
          console.error('Failed to get Clerk token:', error)
          return null
        }
      },
      user: user
        ? {
            id: user.id,
            firstName: user.firstName,
            fullName: user.fullName,
          }
        : null,
      isPreviewMode: false,
    }),
    [getToken, isLoaded, isSignedIn, user],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

function UnconfiguredAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded: true,
      isSignedIn: false,
      getToken: async () => null,
      user: null,
      isPreviewMode: false,
    }),
    [],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
  if (previewEnabled) {
    return <PreviewAuthProvider>{children}</PreviewAuthProvider>
  }

  if (!clerkPubKey) {
    console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY - UI auth is disabled')
    return <UnconfiguredAuthProvider>{children}</UnconfiguredAuthProvider>
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  )
}

export function useAppAuth() {
  const context = useContext(AppAuthContext)
  if (!context) {
    throw new Error('useAppAuth must be used inside AppAuthProvider')
  }
  return context
}

export function AuthUserButton() {
  const { isPreviewMode, user } = useAppAuth()

  if (isPreviewMode) {
    return (
      <div className="rounded-full border px-3 py-1 text-sm text-muted-foreground">
        Preview: {user?.fullName ?? user?.firstName ?? 'User'}
      </div>
    )
  }

  if (!clerkPubKey) {
    return (
      <div className="rounded-full border px-3 py-1 text-sm text-muted-foreground">
        Auth 未設定
      </div>
    )
  }

  return <UserButton afterSignOutUrl="/login" />
}

export function LoginContent() {
  const { isPreviewMode } = useAppAuth()

  if (isPreviewMode) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          Preview mode が有効です。ログインなしで UI を確認できます。
        </p>
        <a
          href="/"
          className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          ダッシュボードへ
        </a>
      </div>
    )
  }

  if (!clerkPubKey) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
        Clerk の公開鍵が未設定です。`VITE_CLERK_PUBLISHABLE_KEY` を設定するか、
        preview 用に `VITE_ENABLE_PREVIEW_AUTH=true` を利用してください。
      </div>
    )
  }

  return (
    <SignIn
      routing="path"
      path="/login"
      signUpUrl="/signup"
      redirectUrl="/"
    />
  )
}
